import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Activity,
  BarChart3,
  HeadphonesIcon,
  BrainCircuit,
  FileText,
  ShieldAlert,
  LogOut,
  Shield,
} from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from '@config/firebase';
import { useAppSelector } from '@store/hooks';
import { cn } from '@utils/cn';
import toast from 'react-hot-toast';

const navItems = [
  { name: 'Overview', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Subscribers', href: '/dashboard/subscribers', icon: Users },
  { name: 'Monitoring', href: '/dashboard/monitoring', icon: Activity },
  { name: 'Analytics', href: '/dashboard/analytics', icon: BarChart3 },
  { name: 'Support', href: '/dashboard/support', icon: HeadphonesIcon },
  { name: 'AI Insights', href: '/dashboard/ai-insights', icon: BrainCircuit },
  { name: 'Changelog', href: '/dashboard/changelog', icon: FileText },
  { name: 'Admin Actions', href: '/dashboard/actions', icon: ShieldAlert },
];

const AdminSidebar = () => {
  const location = useLocation();
  const { userProfile } = useAppSelector(state => state.auth);

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch {
      toast.error('Failed to sign out');
    }
  };

  const isActiveRoute = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <aside className="fixed left-0 top-0 h-screen w-56 bg-neutral-light border-r border-gray-200 flex flex-col font-sans z-30">
      {/* Header */}
      <div className="flex items-center gap-2 p-4 pb-2">
        <Shield size={20} className="text-primary-600" />
        <span className="font-semibold text-gray-900">Drahft Admin</span>
        <span className="ml-1 px-1.5 py-0.5 text-[10px] font-medium bg-primary-100 text-primary-600 rounded-full">
          Internal
        </span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-2">
        <ul className="space-y-0.5">
          {navItems.map(item => {
            const isActive = isActiveRoute(item.href);
            return (
              <li key={item.name}>
                <Link
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                    isActive
                      ? 'bg-primary-100 text-primary-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  )}
                >
                  <item.icon size={18} className="flex-shrink-0" />
                  <span>{item.name}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className="border-t border-gray-200 p-3">
        {userProfile && (
          <p className="text-xs text-gray-400 truncate px-3 mb-2">{userProfile.email}</p>
        )}
        <button
          type="button"
          onClick={handleLogout}
          className="flex w-full items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
        >
          <LogOut size={18} className="flex-shrink-0" />
          <span>Sign out</span>
        </button>
      </div>
    </aside>
  );
};

export default AdminSidebar;
